import { useEffect, useRef, useState } from 'react';
import { PIPELINE_ACTIVE } from './pipelineConstants';

const MIN_FETCH_MS = 5000;
const DISPLAY_TICK_MS = 60000;
const GRACE_AFTER_DUE_MS = 3000;

export function parseTimestamp(value) {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

export function stepNeedsDataRefresh(step, nowMs = Date.now()) {
  if (!step) return false;
  if (PIPELINE_ACTIVE.has(String(step.status || '').toLowerCase())) {
    return true;
  }
  const schedule = step.schedule;
  if (!schedule?.enabled || !schedule.next_run_at) {
    return false;
  }
  const nextMs = parseTimestamp(schedule.next_run_at);
  if (nextMs == null) return false;
  return nextMs + GRACE_AFTER_DUE_MS <= nowMs;
}

export function earliestRefreshDelayMs(steps, nowMs = Date.now()) {
  let delay = DISPLAY_TICK_MS;
  for (const step of steps || []) {
    if (PIPELINE_ACTIVE.has(String(step.status || '').toLowerCase())) {
      return MIN_FETCH_MS;
    }
    const schedule = step.schedule;
    if (!schedule?.enabled || !schedule.next_run_at) {
      continue;
    }
    const nextMs = parseTimestamp(schedule.next_run_at);
    if (nextMs == null) {
      continue;
    }
    const untilDue = nextMs + GRACE_AFTER_DUE_MS - nowMs;
    if (untilDue <= 0) {
      return MIN_FETCH_MS;
    }
    delay = Math.min(delay, untilDue);
  }
  return Math.max(MIN_FETCH_MS, Math.min(delay, DISPLAY_TICK_MS));
}

/**
 * Keeps pipeline cards current: re-renders relative times on a timer and silently
 * refetches dashboard data when a step is active or its next_run_at has passed.
 */
export function usePipelineAutoRefresh(steps, { onRefresh, hasActiveJob }) {
  const [displayTick, setDisplayTick] = useState(0);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    const timer = setInterval(() => {
      setDisplayTick((t) => t + 1);
    }, DISPLAY_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!steps?.length) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId;

    const runCycle = async () => {
      if (cancelled) {
        return;
      }
      const now = Date.now();
      const needsFetch = Boolean(hasActiveJob)
        || steps.some((step) => stepNeedsDataRefresh(step, now));
      if (needsFetch && onRefreshRef.current) {
        await onRefreshRef.current();
      }
      if (cancelled) {
        return;
      }
      timeoutId = setTimeout(runCycle, earliestRefreshDelayMs(steps, Date.now()));
    };

    timeoutId = setTimeout(runCycle, earliestRefreshDelayMs(steps));

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [steps, hasActiveJob]);

  return displayTick;
}
